/**
 * CashRegisterQRModal
 *
 * On-screen QR presentation of the Fiscat cashier payload for iOS/iPad (and any
 * platform where a direct Bluetooth/WiFi link is unavailable or has failed).
 * The Fiscat register scans the code with its built-in scanner — the exact same
 * `fiscat/AEE|…` bytes that Android pushes over Bluetooth.
 *
 * Mounted once near the app root (beside {@link ./CashRegisterRetryDialog}); the
 * transport layer opens it via {@link ../helpers/CashRegisterQRModalService}.
 * The modal is the retry surface itself: it stays open until the operator
 * confirms the scan (**Done** → resolve `true`) or defers it (**Later** →
 * resolve `false`, payload persisted for re-send from the order details screen).
 *
 * The `react-native-qrcode-svg` dependency is lazily required and guarded so a
 * binary that predates it never crashes — the payload is shown as copyable text
 * instead.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Dimensions, Modal, ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {registerCashRegisterQRModalHandler} from '../helpers/CashRegisterQRModalService';

const {width} = Dimensions.get('window');
const QR_SIZE = Math.min(Math.round(width * 0.66), 320);

/**
 * Lazily resolve the optional QR renderer so its absence degrades gracefully.
 *
 * @returns {?React.ComponentType} The `QRCode` component or `null`.
 */
const loadQRCodeComponent = () => {
    try {
        const mod = require('react-native-qrcode-svg');

        return mod?.default || mod || null;
    } catch (_error) {
        return null;
    }
};

const QRCode = loadQRCodeComponent();

/**
 * Global host for the Fiscat QR modal. Renders nothing until opened.
 *
 * @returns {?React.ReactElement}
 */
const CashRegisterQRModal = () => {
    const {t} = useTranslation();
    const [visible, setVisible] = useState(false);
    const [payload, setPayload] = useState('');
    const [orderCode, setOrderCode] = useState('');
    const resolveRef = useRef(null);

    const settle = useCallback((result) => {
        setVisible(false);
        setPayload('');
        setOrderCode('');
        if (resolveRef.current) {
            resolveRef.current(result);
            resolveRef.current = null;
        }
    }, []);

    const openModal = useCallback((options = {}) => new Promise((resolve) => {
        // If a previous request is still open, defer it before replacing.
        if (resolveRef.current) {
            resolveRef.current(false);
        }
        resolveRef.current = resolve;
        setPayload(String(options.payload || ''));
        setOrderCode(String(options.orderCode || ''));
        setVisible(true);
    }), []);

    useEffect(() => {
        const unregister = registerCashRegisterQRModalHandler(openModal);

        return () => {
            unregister();
            if (resolveRef.current) {
                resolveRef.current(false);
                resolveRef.current = null;
            }
        };
    }, [openModal]);

    const handleConfirm = useCallback(() => settle(true), [settle]);
    const handleDefer = useCallback(() => settle(false), [settle]);

    const canRenderQR = useMemo(() => Boolean(QRCode && payload), [payload]);

    if (!visible) {
        return null;
    }

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={handleDefer}>
            <View style={styles.backdrop}>
                <View style={styles.card}>
                    <Text style={styles.title}>{t('cash_register_qr_title')}</Text>
                    {orderCode ? (
                        <Text style={styles.orderCode}>{t('cash_register_order_label', {code: orderCode})}</Text>
                    ) : null}
                    <Text style={styles.message}>{t('cash_register_qr_instruction')}</Text>

                    <View style={styles.qrWrapper}>
                        {canRenderQR ? (
                            <QRCode value={payload} size={QR_SIZE} ecl="M" quietZone={8} />
                        ) : (
                            <ScrollView style={styles.payloadFallback} contentContainerStyle={styles.payloadFallbackContent}>
                                <Text style={styles.payloadLabel}>{t('cash_register_qr_render_unavailable')}</Text>
                                <Text selectable style={styles.payloadText}>{payload}</Text>
                            </ScrollView>
                        )}
                    </View>

                    <TouchableOpacity style={styles.confirmButton} onPress={handleConfirm}>
                        <Text style={styles.confirmButtonText}>{t('cash_register_qr_confirm_scanned')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.deferButton} onPress={handleDefer}>
                        <Text style={styles.deferButtonText}>{t('cash_register_close_retry_later')}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        padding: 24,
    },
    card: {
        width: Math.min(width * 0.9, 480),
        borderRadius: 22,
        padding: 28,
        alignItems: 'center',
        backgroundColor: '#fff',
        borderWidth: 4,
        borderColor: '#F76E24',
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.35,
        shadowRadius: 16,
        elevation: 16,
    },
    title: {
        fontSize: 26,
        fontWeight: '900',
        color: '#2B2B2B',
        textAlign: 'center',
        marginBottom: 6,
    },
    orderCode: {
        fontSize: 20,
        fontWeight: '800',
        color: '#F76E24',
        marginBottom: 10,
    },
    message: {
        fontSize: 17,
        lineHeight: 24,
        color: '#333',
        textAlign: 'center',
        marginBottom: 18,
    },
    qrWrapper: {
        padding: 16,
        borderRadius: 16,
        backgroundColor: '#fff',
        borderWidth: 1,
        borderColor: '#EEE',
        marginBottom: 22,
        alignItems: 'center',
        justifyContent: 'center',
    },
    payloadFallback: {
        maxHeight: QR_SIZE,
        width: QR_SIZE,
    },
    payloadFallbackContent: {
        padding: 8,
    },
    payloadLabel: {
        color: '#D90429',
        fontWeight: '800',
        marginBottom: 8,
        textAlign: 'center',
    },
    payloadText: {
        color: '#2B2B2B',
        fontSize: 12,
        fontFamily: 'monospace',
    },
    confirmButton: {
        width: '100%',
        borderRadius: 14,
        paddingVertical: 18,
        paddingHorizontal: 16,
        backgroundColor: '#F76E24',
        alignItems: 'center',
        justifyContent: 'center',
    },
    confirmButtonText: {
        color: '#fff',
        fontSize: 20,
        fontWeight: '900',
        textAlign: 'center',
    },
    deferButton: {
        marginTop: 14,
        paddingVertical: 12,
        paddingHorizontal: 16,
    },
    deferButtonText: {
        color: '#555',
        fontSize: 16,
        fontWeight: '700',
        textAlign: 'center',
    },
});

export default CashRegisterQRModal;
